'use client';

/**
 * Conversation — adapted from AI Elements (https://elements.ai-sdk.dev/components/conversation)
 *
 * Simplified for current needs. Download helpers can be re-added later.
 */

import React, { type ComponentProps, type ReactNode, useCallback } from 'react';
import {
  StickToBottom,
  useStickToBottomContext,
  type StickToBottomContext,
  type StickToBottomProps,
} from 'use-stick-to-bottom';
import { Button } from '../../button/button';
import { cn } from '../../../lib/utils/cn';
import { ArrowDown } from '@bridebook/aura/src/icons';
import { useKeepEndInView } from './use-keep-end-in-view';

export type ConversationProps = StickToBottomProps;

export const Conversation = ({ className, ...props }: ConversationProps) => (
  <StickToBottom
    className={cn('relative flex-1 overflow-y-hidden', className)}
    initial="smooth"
    resize="smooth"
    {...props}
  />
);

export type ConversationContentProps = Omit<ComponentProps<'div'>, 'children'> & {
  children: ReactNode | ((context: StickToBottomContext) => ReactNode);
  scrollClassName?: string;
};

export const ConversationContent = ({ className, ...props }: ConversationContentProps) => {
  useKeepEndInView();

  return (
    <StickToBottom.Content
      role="log"
      className={cn('flex flex-col gap-8 p-4', className)}
      {...props}
    />
  );
};

export type ConversationEmptyStateProps = ComponentProps<'div'> & {
  title?: string;
  description?: string;
  icon?: ReactNode;
};

export type ConversationScrollButtonProps = ComponentProps<typeof Button>;

export const ConversationScrollButton = ({
  className,
  onClick,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleScrollToBottom = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      scrollToBottom();
    },
    [scrollToBottom, onClick],
  );

  if (isAtBottom) return null;

  return (
    <Button
      aria-label="Scroll to latest message"
      className={cn('absolute bottom-4 left-1/2 z-10 -translate-x-1/2 shadow-md', className)}
      icon={ArrowDown}
      onClick={handleScrollToBottom}
      size="sm"
      type="button"
      variant="secondary"
      {...props}
    />
  );
};
